import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, apiUrls } from '@/lib/apiClient';
import type { TocAcceptance } from '@/types/model.types';

export const TOC_QUERY_KEY = ['toc-acceptance'] as const;

/**
 * Replaces TocAcceptanceService, including its hand-written in-memory cache and
 * clearCache(). Note the behaviour change: the Angular version treated a failed
 * request as "not accepted" and redirected to the acceptance page. Here a
 * failure surfaces as an error so a network blip doesn't ask the user to
 * re-accept terms they already accepted.
 */
export function useTocAcceptance() {
  const query = useQuery({
    queryKey: TOC_QUERY_KEY,
    queryFn: () =>
      api.get<TocAcceptance>(apiUrls.tocAcceptance, { operation: 'checking terms acceptance' }),
    staleTime: Infinity, // acceptance doesn't change without the user acting
  });

  return { ...query, hasAccepted: Boolean(query.data?.timestamp) };
}

export function useAcceptTerms() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api.put<TocAcceptance>(apiUrls.tocAcceptance, { operation: 'accepting terms', body: {} }),
    onSuccess: (data) => {
      queryClient.setQueryData(TOC_QUERY_KEY, data);
    },
  });
}
